import axios from 'axios';
import { useEffect, useState } from 'react';

import { getToken } from './token';
import { getUserRole, ROLES } from './consts/roles';

/**
 * Текущий пользователь с сервера: роль, права и его группы в журнале.
 *
 * Роль лежит и в токене, но права и список групп туда не помещаются и устаревают при первом
 * же изменении: выдали право — а человек всё ещё ходит со старым токеном. Поэтому источник
 * истины — `GET /api/auth/me`, тот же, по которому решает сервер.
 *
 * Ответ кэшируется на время жизни вкладки: его спрашивают несколько компонентов сразу,
 * а сервер однопроцессорный.
 */
let request = null;   // промис в полёте — чтобы параллельные вызовы не плодили запросы
let resolved = null;  // уже полученный ответ — чтобы повторный монтаж не мигал

export const resetCurrentUser = () => { request = null; resolved = null; };

export const getCachedUser = () => resolved;

export const loadCurrentUser = () => {
  if (!getToken()) return Promise.resolve(null);
  if (resolved) return Promise.resolve(resolved);

  if (!request) {
    request = axios
      .get(`${process.env.REACT_APP_URL_PATH}/api/auth/me`, {
        headers: { Authorization: getToken() },
      })
      .then((resp) => {
        resolved = resp.data;
        return resolved;
      })
      .catch((error) => {
        // Сбрасываем кэш, чтобы следующий вызов попробовал снова:
        // иначе одна сетевая ошибка навсегда оставит интерфейс без прав.
        request = null;
        throw error;
      });
  }

  return request;
};

/**
 * @returns {{ user: object|null, loading: boolean, can: (permission: string) => boolean }}
 *
 * Пока ответ не пришёл, `can` отвечает по роли из токена — иначе кнопки успевают мигнуть
 * недоступными на каждой загрузке страницы.
 */
export const useCurrentUser = () => {
  const [user, setUser] = useState(resolved);
  const [loading, setLoading] = useState(!resolved);

  useEffect(() => {
    let alive = true;

    loadCurrentUser()
      .then((data) => { if (alive) setUser(data); })
      .catch(() => { if (alive) setUser(null); })
      .finally(() => { if (alive) setLoading(false); });

    return () => { alive = false; };
  }, []);

  const can = (permission) => {
    if (user) return (user.permissions || []).includes(permission);

    // Запасной вариант на время загрузки: администратор и редактор в журнале могут всё.
    const role = getUserRole();
    return role === ROLES.ADMIN || role === ROLES.EDITOR;
  };

  return { user, loading, can };
};

export const PERMISSIONS = {
  STUDENTS_READ: 'STUDENTS_READ',
  STUDENTS_WRITE: 'STUDENTS_WRITE',
  MAIL_SEND: 'MAIL_SEND',
  MAIL_RECTOR_APPROVAL_SEND: 'MAIL_RECTOR_APPROVAL_SEND',
  JOURNAL_READ_ALL: 'JOURNAL_READ_ALL',
  JOURNAL_READ_OWN: 'JOURNAL_READ_OWN',
  JOURNAL_MARK_WRITE: 'JOURNAL_MARK_WRITE',
  JOURNAL_MANAGE: 'JOURNAL_MANAGE',
};
